import { Button, Checkbox, Dialog, Input, Select, Textarea } from '@artosera/ui';
import { TYPES_OEUVRE, manquesDeLOeuvre, type ManqueOeuvre } from '@artosera/shared';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useArtistes } from '../../../api/artistes.js';
import {
  oeuvresApi,
  useModifierOeuvre,
  type CorpsMiseAJourOeuvre,
  type OeuvrePresentee,
} from '../../../api/oeuvres.js';
import { ManquesFiche } from '../ManquesFiche.js';
import { Field } from '../../Field.js';
import { GardeSaisie } from '../../GardeSaisie.js';
import { useErreurFormulaire } from '../../useErreurFormulaire.js';
import { useFermetureGardee } from '../../useFermetureGardee.js';
import { LIMITES, erreurAnnee, erreurDimension } from '../../validations.js';
import styles from './Tabs.module.css';

export interface InformationsTabProps {
  galerieSlug: string;
  oeuvre: OeuvrePresentee;
  /** Droit d'écriture (`artworks:write`, § 4) : sans lui, aucun champ ni bouton « Modifier » (ARTO-53). */
  peutEcrire: boolean;
  /**
   * Module `editions-provenance` (D21) : sans lui, on ne fait pas d'une œuvre
   * une édition et le tirage d'une édition existante ne se modifie pas.
   */
  editionsOuvertes?: boolean;
}

/**
 * Informations (3.3, écran 2, ARTO-53) : consultation éditoriale par défaut
 * — le titre, l'artiste, la technique et les dimensions vivent déjà dans le
 * cartel au-dessus, cet onglet ajoute le type et son tirage, les étiquettes
 * et les descriptions FR/EN. Le formulaire complet ne s'affiche qu'après
 * « Modifier », et seulement pour qui a le droit d'écrire (audit du
 * 23 septembre 2026, § 1 : « un long formulaire est affiché immédiatement »).
 */
export function InformationsTab({
  galerieSlug,
  oeuvre,
  peutEcrire,
  editionsOuvertes = true,
}: InformationsTabProps): JSX.Element {
  const [params] = useSearchParams();
  const [mode, setMode] = useState<'lecture' | 'edition'>(
    params.has('champ') ? 'edition' : 'lecture',
  );
  const [vientDEtreEnregistree, setVientDEtreEnregistree] = useState(false);

  if (mode === 'lecture' || !peutEcrire) {
    return (
      <InformationsLecture
        oeuvre={oeuvre}
        peutEcrire={peutEcrire}
        vientDEtreEnregistree={vientDEtreEnregistree}
        onModifier={() => {
          setVientDEtreEnregistree(false);
          setMode('edition');
        }}
      />
    );
  }

  return (
    <InformationsEdition
      galerieSlug={galerieSlug}
      oeuvre={oeuvre}
      editionsOuvertes={editionsOuvertes}
      onAnnuler={() => setMode('lecture')}
      onEnregistree={() => {
        setMode('lecture');
        setVientDEtreEnregistree(true);
      }}
    />
  );
}

function InformationsLecture({
  oeuvre,
  peutEcrire,
  vientDEtreEnregistree,
  onModifier,
}: {
  oeuvre: OeuvrePresentee;
  peutEcrire: boolean;
  vientDEtreEnregistree: boolean;
  onModifier: () => void;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'oeuvres', 'commun']);
  const oui = t('regie:oeuvres.informations.oui');
  const non = t('regie:oeuvres.informations.non');
  const tirageResume =
    oeuvre.type === 'edition' && oeuvre.tirage
      ? [
          `${String(oeuvre.tirage.taille)} ${t(`oeuvres:types.edition`)}`,
          oeuvre.tirage.epreuvesArtiste ? `${String(oeuvre.tirage.epreuvesArtiste)} EA` : null,
          oeuvre.tirage.epreuvesEssai
            ? `${String(oeuvre.tirage.epreuvesEssai)} ${t('regie:oeuvres.informations.epreuvesEssai')}`
            : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : null;

  return (
    <div className={styles.consultation} data-ui="artwork-overview">
      <dl className={styles.donneesGrille} data-ui="artwork-fields">
        <div>
          <dt>{t('regie:oeuvres.informations.type')}</dt>
          <dd>{t(`oeuvres:types.${oeuvre.type}`)}</dd>
        </div>
        {tirageResume ? (
          <div>
            <dt>{t('oeuvres:champs.tirage')}</dt>
            <dd>{tirageResume}</dd>
          </div>
        ) : null}
        <div>
          <dt>{t('oeuvres:champs.visibleSurLeSite')}</dt>
          <dd>{oeuvre.visibleSurLeSite ? oui : non}</dd>
        </div>
        <div>
          <dt>{t('oeuvres:champs.prixVisible')}</dt>
          <dd>{oeuvre.prixVisible ? oui : non}</dd>
        </div>
      </dl>

      {oeuvre.etiquettes.length > 0 ? (
        <ul className={styles.etiquettesListe}>
          {oeuvre.etiquettes.map((etiquette) => (
            <li key={etiquette}>{etiquette}</li>
          ))}
        </ul>
      ) : null}

      <div className={styles.descriptionBloc} data-ui="artwork-description">
        <h2>{t('regie:oeuvres.informations.descriptionFr')}</h2>
        <p>{oeuvre.description?.fr || t('regie:oeuvres.informations.aucuneDescription')}</p>
      </div>

      {oeuvre.description?.en ? (
        <div className={styles.descriptionBloc} data-ui="artwork-description">
          <h2>{t('regie:oeuvres.informations.descriptionEn')}</h2>
          <p>{oeuvre.description.en}</p>
        </div>
      ) : null}

      {peutEcrire && oeuvre.notesInternes ? (
        <div className={styles.descriptionBloc} data-ui="artwork-notes">
          <h2>{t('oeuvres:champs.notesInternes')}</h2>
          <p>{oeuvre.notesInternes}</p>
        </div>
      ) : null}

      {vientDEtreEnregistree ? (
        <p className={styles.succes}>{t('oeuvres:messages.modifiee')}</p>
      ) : null}

      {peutEcrire ? (
        <Button variant="secondary" onClick={onModifier}>
          {t('commun:actions.modifier')}
        </Button>
      ) : null}
    </div>
  );
}

function InformationsEdition({
  galerieSlug,
  oeuvre,
  editionsOuvertes,
  onAnnuler,
  onEnregistree,
}: {
  galerieSlug: string;
  oeuvre: OeuvrePresentee;
  editionsOuvertes: boolean;
  onAnnuler: () => void;
  onEnregistree: () => void;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'oeuvres', 'commun']);
  const artistes = useArtistes(galerieSlug, { tri: 'nom', parPage: 200 });
  const modifier = useModifierOeuvre(galerieSlug);

  const [titre, setTitre] = useState(oeuvre.titre);
  const [artisteId, setArtisteId] = useState(oeuvre.artisteId);
  const [annee, setAnnee] = useState(oeuvre.annee?.toString() ?? '');
  const [techniqueFr, setTechniqueFr] = useState(oeuvre.technique?.fr ?? '');
  const [techniqueEn, setTechniqueEn] = useState(oeuvre.technique?.en ?? '');
  const [hauteur, setHauteur] = useState(oeuvre.dimensions?.hauteur.toString() ?? '');
  const [largeur, setLargeur] = useState(oeuvre.dimensions?.largeur.toString() ?? '');
  const [profondeur, setProfondeur] = useState(oeuvre.dimensions?.profondeur?.toString() ?? '');
  const [type, setType] = useState(oeuvre.type);
  const [tailleTirage, setTailleTirage] = useState(oeuvre.tirage?.taille.toString() ?? '');
  const [epreuvesArtiste, setEpreuvesArtiste] = useState(
    oeuvre.tirage?.epreuvesArtiste.toString() ?? '0',
  );
  const [epreuvesEssai, setEpreuvesEssai] = useState(
    oeuvre.tirage?.epreuvesEssai.toString() ?? '0',
  );
  const [descriptionFr, setDescriptionFr] = useState(oeuvre.description?.fr ?? '');
  const [descriptionEn, setDescriptionEn] = useState(oeuvre.description?.en ?? '');
  const [notesInternes, setNotesInternes] = useState(oeuvre.notesInternes);
  const [etiquettes, setEtiquettes] = useState(oeuvre.etiquettes.join(', '));
  const [visibleSurLeSite, setVisibleSurLeSite] = useState(oeuvre.visibleSurLeSite);
  const [prixVisible, setPrixVisible] = useState(oeuvre.prixVisible);
  const [params] = useSearchParams();
  useEffect(() => {
    const champ = params.get('champ');
    if (champ) document.getElementById(champ)?.focus();
  }, [params]);
  const [confirmation, setConfirmation] = useState<{
    corps: CorpsMiseAJourOeuvre;
    manques: readonly ManqueOeuvre[];
  } | null>(null);
  const [verification, setVerification] = useState(false);
  const formulaire = useErreurFormulaire();
  // Garde de saisie : écart entre ce qui est affiché et ce qui a été chargé.
  const saisie = JSON.stringify([
    titre,
    artisteId,
    annee,
    techniqueFr,
    techniqueEn,
    hauteur,
    largeur,
    profondeur,
    type,
    tailleTirage,
    epreuvesArtiste,
    epreuvesEssai,
    descriptionFr,
    descriptionEn,
    notesInternes,
    etiquettes,
    visibleSurLeSite,
    prixVisible,
  ]);
  const [saisieInitiale] = useState(saisie);
  const modifie = saisie !== saisieInitiale;
  const gardeAnnuler = useFermetureGardee(modifie, (ouvert) => {
    if (!ouvert) onAnnuler();
  });

  // Pas d'effet de réinitialisation : `OeuvreDetailPage` monte cet onglet
  // avec `key={oeuvre.id}`, donc un changement d'œuvre remonte un formulaire
  // neuf plutôt que de resynchroniser un état existant depuis un effet.

  const erreurSaisie = (cle: ReturnType<typeof erreurAnnee>, saisie: string): string | undefined =>
    saisie.trim() && cle ? t(cle) : undefined;
  const erreurAnneeSaisie = erreurSaisie(erreurAnnee(annee), annee);
  const erreurHauteur = erreurSaisie(erreurDimension(hauteur), hauteur);
  const erreurLargeur = erreurSaisie(erreurDimension(largeur), largeur);
  const erreurProfondeur = erreurSaisie(erreurDimension(profondeur), profondeur);
  const saisieValide = !erreurAnneeSaisie && !erreurHauteur && !erreurLargeur && !erreurProfondeur;
  const dimensionIncomplete = Boolean(hauteur.trim()) !== Boolean(largeur.trim());

  async function enregistrer(): Promise<void> {
    if (!saisieValide) return;
    formulaire.reinitialiser();
    try {
      const corps: CorpsMiseAJourOeuvre = {
        titre,
        artisteId,
        ...(annee ? { annee: Number(annee) } : {}),
        technique: { fr: techniqueFr, ...(techniqueEn ? { en: techniqueEn } : {}) },
        ...(hauteur && largeur
          ? {
              dimensions: {
                hauteur: Number(hauteur),
                largeur: Number(largeur),
                ...(profondeur ? { profondeur: Number(profondeur) } : {}),
              },
            }
          : {}),
        type,
        ...(type === 'edition' && tailleTirage
          ? {
              tirage: {
                taille: Number(tailleTirage),
                epreuvesArtiste: Number(epreuvesArtiste || '0'),
                epreuvesEssai: Number(epreuvesEssai || '0'),
              },
            }
          : {}),
        description: { fr: descriptionFr, ...(descriptionEn ? { en: descriptionEn } : {}) },
        notesInternes,
        etiquettes: etiquettes
          .split(',')
          .map((valeur) => valeur.trim())
          .filter(Boolean),
        visibleSurLeSite,
        prixVisible,
      };
      if (visibleSurLeSite && !oeuvre.visibleSurLeSite) {
        setVerification(true);
        const fraiche = await oeuvresApi.obtenir(galerieSlug, oeuvre.id);
        if (!fraiche.contexteCompletude)
          throw new Error(t('regie:oeuvres.completude.indisponible'));
        const manques = manquesDeLOeuvre(
          { ...fraiche.oeuvre, ...corps },
          'site',
          fraiche.contexteCompletude,
        );
        if (manques.length > 0) {
          setConfirmation({ corps, manques });
          return;
        }
      }
      await modifier.mutateAsync({ id: oeuvre.id, corps });
      onEnregistree();
    } catch (error) {
      formulaire.appliquer(error, [
        'titre',
        'artisteId',
        'annee',
        'technique',
        'dimensions',
        'tirage',
        'description',
        'notesInternes',
        'etiquettes',
      ]);
    } finally {
      setVerification(false);
    }
  }

  return (
    <form
      className={styles.formulaire}
      data-ui="artwork-form"
      onSubmit={(event) => event.preventDefault()}
      noValidate
    >
      <GardeSaisie actif={modifie} />
      {confirmation ? (
        <Dialog
          open
          title={t('regie:oeuvres.completude.titre')}
          closeLabel={t('commun:actions.fermer')}
          onOpenChange={(ouvert) => {
            if (!ouvert) setConfirmation(null);
          }}
        >
          <ManquesFiche manques={confirmation.manques} />
          <Button variant="secondary" onClick={() => setConfirmation(null)}>
            {t('regie:oeuvres.completude.revenir')}
          </Button>
          {confirmation.manques.some((manque) => manque.bloquant) ? (
            <p role="alert">{t('regie:oeuvres.completude.alerte')}</p>
          ) : (
            <Button
              loading={modifier.isPending || verification}
              onClick={() => {
                void modifier
                  .mutateAsync({ id: oeuvre.id, corps: confirmation.corps })
                  .then(onEnregistree)
                  .catch((erreur: unknown) => {
                    formulaire.appliquer(erreur, []);
                    setConfirmation(null);
                  });
              }}
            >
              {t('regie:oeuvres.completude.publier')}
            </Button>
          )}
        </Dialog>
      ) : null}
      {gardeAnnuler.confirmation}
      <div className={styles.grille2}>
        <Field
          label={t('oeuvres:champs.titre')}
          htmlFor="info-titre"
          error={formulaire.messageChamp('titre')}
        >
          <Input
            id="info-titre"
            maxLength={LIMITES.oeuvre.titre}
            value={titre}
            onChange={(event) => setTitre(event.target.value)}
          />
        </Field>
        <Field
          label={t('oeuvres:champs.artiste')}
          htmlFor="info-artiste"
          error={formulaire.messageChamp('artisteId')}
        >
          <Select
            id="info-artiste"
            aria-label={t('oeuvres:champs.artiste')}
            value={artisteId}
            onValueChange={setArtisteId}
            options={
              artistes.data?.artistes.map((artiste) => ({
                value: artiste.id,
                label: artiste.nom,
              })) ?? [{ value: artisteId, label: oeuvre.artisteNom }]
            }
          />
        </Field>
        <Field
          label={t('oeuvres:champs.annee')}
          htmlFor="info-annee"
          error={erreurAnneeSaisie ?? formulaire.messageChamp('annee')}
        >
          <Input
            id="info-annee"
            mono
            type="number"
            inputMode="numeric"
            min={0}
            max={2999}
            step={1}
            value={annee}
            onChange={(event) => setAnnee(event.target.value)}
          />
        </Field>
        <Field
          label={t('regie:oeuvres.informations.etiquettes')}
          htmlFor="info-etiquettes"
          error={formulaire.messageChamp('etiquettes')}
        >
          <Input
            id="info-etiquettes"
            value={etiquettes}
            onChange={(event) => setEtiquettes(event.target.value)}
          />
        </Field>
      </div>

      <div className={styles.grille2}>
        <Field
          label={t('regie:oeuvres.informations.techniqueFr')}
          htmlFor="info-technique-fr"
          error={formulaire.messageChamp('technique.fr', 'technique')}
        >
          <Input
            id="info-technique-fr"
            value={techniqueFr}
            onChange={(event) => setTechniqueFr(event.target.value)}
          />
        </Field>
        <Field label={t('regie:oeuvres.informations.techniqueEn')} htmlFor="info-technique-en">
          <Input
            id="info-technique-en"
            value={techniqueEn}
            onChange={(event) => setTechniqueEn(event.target.value)}
          />
        </Field>
      </div>

      <div className={styles.grille3}>
        <Field
          label={t('regie:oeuvres.informations.dimensionsHauteur')}
          htmlFor="info-hauteur"
          error={
            erreurHauteur ??
            (dimensionIncomplete && !hauteur.trim()
              ? t('regie:oeuvres.informations.dimensionsIncomplete')
              : formulaire.messageChamp('dimensions.hauteur', 'dimensions'))
          }
        >
          <Input
            id="info-hauteur"
            mono
            type="number"
            min={0}
            step="any"
            value={hauteur}
            onChange={(event) => setHauteur(event.target.value)}
          />
        </Field>
        <Field
          label={t('regie:oeuvres.informations.dimensionsLargeur')}
          htmlFor="info-largeur"
          error={
            erreurLargeur ??
            (dimensionIncomplete && !largeur.trim()
              ? t('regie:oeuvres.informations.dimensionsIncomplete')
              : formulaire.messageChamp('dimensions.largeur'))
          }
        >
          <Input
            id="info-largeur"
            mono
            type="number"
            min={0}
            step="any"
            value={largeur}
            onChange={(event) => setLargeur(event.target.value)}
          />
        </Field>
        <Field
          label={t('regie:oeuvres.informations.dimensionsProfondeur')}
          htmlFor="info-profondeur"
          error={erreurProfondeur ?? formulaire.messageChamp('dimensions.profondeur')}
        >
          <Input
            id="info-profondeur"
            mono
            type="number"
            min={0}
            step="any"
            value={profondeur}
            onChange={(event) => setProfondeur(event.target.value)}
          />
        </Field>
      </div>

      <div className={styles.grille2}>
        <Field label={t('regie:oeuvres.informations.type')} htmlFor="info-type">
          <Select
            id="info-type"
            aria-label={t('regie:oeuvres.informations.type')}
            value={type}
            onValueChange={(valeur) => setType(valeur as (typeof TYPES_OEUVRE)[number])}
            options={TYPES_OEUVRE.filter(
              (code) => code !== 'edition' || editionsOuvertes || oeuvre.type === 'edition',
            ).map((code) => ({
              value: code,
              label: t(`oeuvres:types.${code}`),
            }))}
          />
        </Field>
        {type === 'edition' ? (
          <Field label={t('regie:oeuvres.informations.tailleTirage')} htmlFor="info-tirage">
            <Input
              id="info-tirage"
              mono
              type="number"
              readOnly={!editionsOuvertes}
              value={tailleTirage}
              onChange={(event) => setTailleTirage(event.target.value)}
            />
          </Field>
        ) : null}
      </div>

      {type === 'edition' ? (
        <div className={styles.grille2}>
          <Field label={t('regie:oeuvres.informations.epreuvesArtiste')} htmlFor="info-ea">
            <Input
              id="info-ea"
              mono
              type="number"
              readOnly={!editionsOuvertes}
              value={epreuvesArtiste}
              onChange={(event) => setEpreuvesArtiste(event.target.value)}
            />
          </Field>
          <Field label={t('regie:oeuvres.informations.epreuvesEssai')} htmlFor="info-ee">
            <Input
              id="info-ee"
              mono
              type="number"
              readOnly={!editionsOuvertes}
              value={epreuvesEssai}
              onChange={(event) => setEpreuvesEssai(event.target.value)}
            />
          </Field>
        </div>
      ) : null}

      <div className={styles.grille2}>
        <Field label={t('regie:oeuvres.informations.descriptionFr')} htmlFor="info-description-fr">
          <Textarea
            id="info-description-fr"
            rows={4}
            value={descriptionFr}
            onChange={(event) => setDescriptionFr(event.target.value)}
          />
        </Field>
        <Field label={t('regie:oeuvres.informations.descriptionEn')} htmlFor="info-description-en">
          <Textarea
            id="info-description-en"
            rows={4}
            value={descriptionEn}
            onChange={(event) => setDescriptionEn(event.target.value)}
          />
        </Field>
      </div>

      <Field label={t('oeuvres:champs.notesInternes')} htmlFor="info-notes">
        <Textarea
          id="info-notes"
          rows={3}
          value={notesInternes}
          onChange={(event) => setNotesInternes(event.target.value)}
        />
      </Field>

      <div className={styles.cases}>
        <Checkbox
          id="info-visible"
          label={t('oeuvres:champs.visibleSurLeSite')}
          checked={visibleSurLeSite}
          onCheckedChange={(valeur) => setVisibleSurLeSite(valeur === true)}
        />
        <Checkbox
          id="info-prix-visible"
          label={t('oeuvres:champs.prixVisible')}
          checked={prixVisible}
          onCheckedChange={(valeur) => setPrixVisible(valeur === true)}
        />
      </div>

      {formulaire.erreur ? (
        <p className={styles.erreur} role="alert">
          {formulaire.erreur}
        </p>
      ) : null}

      <div className={styles.actionsEdition}>
        <Button variant="ghost" type="button" onClick={() => gardeAnnuler.onOpenChange(false)}>
          {t('commun:actions.annuler')}
        </Button>
        <Button
          onClick={() => void enregistrer()}
          loading={modifier.isPending || verification}
          disabled={!saisieValide || dimensionIncomplete}
        >
          {t('commun:actions.enregistrer')}
        </Button>
      </div>
    </form>
  );
}
